import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { FileText, Plus, Pin, X } from 'lucide-react';
import { Button } from '../ui/button';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

const PASTEL_COLORS = [
  { label: 'Yellow', value: '#fef9c3' },
  { label: 'Blue',   value: '#dbeafe' },
  { label: 'Pink',   value: '#fce7f3' },
  { label: 'Green',  value: '#dcfce7' },
  { label: 'Purple', value: '#f3e8ff' },
];

const NotesWidget = () => {
  const { user, refreshTick } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ content: '', color: '#fef9c3' });
  const [saving, setSaving] = useState(false);

  const fetchNotes = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('user_id', user.id)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      setNotes(data ?? []);
    } catch (err) {
      console.error('NotesWidget fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    fetchNotes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, refreshTick]);

  const handleCreate = async () => {
    if (!form.content.trim()) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('notes').insert({
        user_id: user.id,
        content: form.content.trim(),
        color: form.color,
      });
      if (error) throw error;
      setShowModal(false);
      setForm({ content: '', color: '#fef9c3' });
      await fetchNotes();
    } catch (err) {
      console.error('NotesWidget create error:', err);
    } finally {
      setSaving(false);
    }
  };

  const togglePin = async (note) => {
    try {
      const { error } = await supabase
        .from('notes')
        .update({ is_pinned: !note.is_pinned })
        .eq('id', note.id);
      if (error) throw error;
      await fetchNotes();
    } catch (err) {
      console.error('NotesWidget pin error:', err);
    }
  };

  return (
    <>
      <Card className="bg-white/75 backdrop-blur-md border border-white/90 shadow-[0_4px_20px_rgba(99,102,241,0.06)] dark:bg-[#111114] dark:border-[#1e1e24] dark:shadow-none hover:shadow-[0_8px_30px_rgba(99,102,241,0.1)] dark:hover:shadow-none transition-all duration-300 overflow-hidden">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-indigo-50/50 dark:border-[#1a1a20]">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-[rgba(99,102,241,0.08)] dark:bg-[rgba(167,139,250,0.1)] rounded-xl">
                <FileText className="text-indigo-500 dark:text-violet-400" size={24} />
              </div>
              <h2 className="text-xl font-semibold text-[#1a1625] dark:text-[#e5e5e5]">Notes</h2>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-indigo-300 hover:text-indigo-500 dark:text-[#555] dark:hover:text-violet-400"
              onClick={() => setShowModal(true)}
            >
              <Plus size={16} />
            </Button>
          </div>

          {/* Notes List */}
          <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 rounded-xl bg-indigo-50/50 dark:bg-[#16161b] animate-pulse" />
                ))}
              </div>
            ) : notes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <FileText size={28} className="text-indigo-200 dark:text-[#333] mb-2" />
                <p className="text-sm text-[#6b7280] dark:text-[#888]">No notes yet</p>
                <p className="text-xs text-[#9ca3af] dark:text-[#555]">Tap + to create one</p>
              </div>
            ) : (
              notes.map((note) => (
                <div
                  key={note.id}
                  className="p-4 rounded-xl transition-all duration-200 cursor-pointer border border-black/5 hover:shadow-md"
                  style={{ backgroundColor: note.color }}
                  onClick={() => togglePin(note)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-xs text-gray-700 line-clamp-3 flex-1">{note.content}</p>
                    {note.is_pinned && (
                      <Pin size={14} className="text-gray-600 ml-2 flex-shrink-0" fill="currentColor" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {new Date(note.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </Card>

      {/* Create Note Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#111114] rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 border border-white/90 dark:border-[#1e1e24]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#1a1625] dark:text-[#e5e5e5]">New Note</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-[#9ca3af] hover:text-[#6b7280] dark:text-[#555] dark:hover:text-[#888] transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-[#6b7280] dark:text-[#888] mb-1 block">Content *</label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  placeholder="Write your note..."
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg border border-indigo-100 dark:border-[#1e1e24] bg-white dark:bg-[#1a1a20] text-sm text-[#1a1625] dark:text-[#e5e5e5] focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:focus:ring-violet-500 resize-none placeholder-[#9ca3af] dark:placeholder-[#555]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#6b7280] dark:text-[#888] mb-2 block">Color</label>
                <div className="flex space-x-2">
                  {PASTEL_COLORS.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => setForm({ ...form, color: c.value })}
                      className={`w-7 h-7 rounded-full border-2 transition-all ${
                        form.color === c.value ? 'border-indigo-400 scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: c.value }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Preview */}
            <div
              className="mt-3 p-3 rounded-xl text-xs text-gray-600 border border-black/5 min-h-[40px]"
              style={{ backgroundColor: form.color }}
            >
              {form.content || <span className="text-gray-400">Preview...</span>}
            </div>

            <div className="flex space-x-2 mt-4">
              <Button
                variant="outline"
                className="flex-1 border-indigo-100 dark:border-[#1e1e24]"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white border-0"
                onClick={handleCreate}
                disabled={saving || !form.content.trim()}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotesWidget;
